import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient as api } from '@plerionhq/design-system';

/**
 * A user's navigation: top-level entry keys in the order they want them, and
 * the keys tucked under the "..." group. Hiding never removes access.
 */
export type NavConfig = {
  order: string[];
  hidden: string[];
};

export type PreferenceSource = 'user' | 'group' | 'tenant' | 'none';

export type ResolvedHome = {
  homeReportId: string | null;
  source: PreferenceSource;
  // Of several groups, the most recently updated one wins.
  sourceGroupId?: string | null;
  // Home and nav resolve independently along user -> group -> tenant.
  navConfig: NavConfig | null;
  navSource: PreferenceSource;
  navSourceGroupId?: string | null;
};

/** Every preference query hangs off this prefix, so one invalidate covers all. */
export const HOME_PREFERENCE_KEY = ['home-preference'];

/** The signed-in user's resolved home. Exported so a caller can wait on its refetch. */
export const RESOLVED_HOME_KEY = [...HOME_PREFERENCE_KEY, 'resolved'];

/**
 * Preferences live with the group or the user they belong to, in
 * service-directory (PL-5985). Each preference is its own sub-resource: a PUT
 * replaces it, a DELETE clears it, and a GET 404s when nothing is set. The
 * tenant is always the session's: the console never names one.
 */
const DIRECTORY_BASE = '/api/base/directory';
const MY_PREFERENCES = `${DIRECTORY_BASE}/users/me/preferences`;

/**
 * The tenant default home dashboard is service-tenant-management's, in the
 * session tenant. Navigation has no tenant default.
 */
const TENANT_PREFERENCES = '/api/base/tenant/preferences';

/** The tenant default home, read unresolved so a report can tell if it is the one. */
export const TENANT_HOME_KEY = [...HOME_PREFERENCE_KEY, 'tenant-home'];

export type TenantHome = {
  tenantId: string;
  homeReportId: string;
  updatedAt: string;
};

const isNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 404;

/** A GET that answers null for an absent preference instead of throwing. */
async function getOrNull<T>(url: string): Promise<T | null> {
  try {
    const response = await api.get<{ data: T }>(url);
    return response.data.data;
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
}

/** A DELETE that treats "already clear" as done. */
async function deleteIfPresent(url: string): Promise<void> {
  try {
    await api.delete(url);
  } catch (error) {
    if (!isNotFound(error)) {
      throw error;
    }
  }
}

/** The signed-in user's resolved home: their choice, else their groups', else the tenant default, else none. */
export function useResolvedHome() {
  return useQuery({
    queryKey: RESOLVED_HOME_KEY,
    queryFn: () =>
      api
        .get<ResolvedHome>(`${MY_PREFERENCES}/resolved`)
        .then((res) => res.data),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Sets the signed-in user's own home. */
export function useSetMyHome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (homeReportId: string) =>
      api.put(`${MY_PREFERENCES}/home-dashboard`, { homeReportId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESOLVED_HOME_KEY });
    },
  });
}

/**
 * Sets the signed-in user's own navigation. Passing null clears it, putting
 * them back on the inherited (group, then tenant) navigation.
 */
export function useSetMyNav() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    NavConfig | null,
    { previous?: ResolvedHome }
  >({
    mutationFn: async (navConfig) => {
      if (navConfig === null) {
        await deleteIfPresent(`${MY_PREFERENCES}/navigation`);
      } else {
        await api.put(`${MY_PREFERENCES}/navigation`, navConfig);
      }
    },
    // Optimistic: the sidebar moves on click, and a failed write rolls back.
    onMutate: async (navConfig) => {
      await queryClient.cancelQueries({ queryKey: RESOLVED_HOME_KEY });
      const previous =
        queryClient.getQueryData<ResolvedHome>(RESOLVED_HOME_KEY);
      if (previous) {
        queryClient.setQueryData<ResolvedHome>(RESOLVED_HOME_KEY, {
          ...previous,
          navConfig,
          navSource: navConfig === null ? 'none' : 'user',
        });
      }
      return { previous };
    },
    onError: (_error, _navConfig, context) => {
      if (context?.previous) {
        queryClient.setQueryData(RESOLVED_HOME_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: RESOLVED_HOME_KEY });
    },
  });
}

/** Removes the signed-in user's own home; they inherit again. */
export function useClearMyHome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteIfPresent(`${MY_PREFERENCES}/home-dashboard`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESOLVED_HOME_KEY });
    },
  });
}

/** The tenant default home, or null when the tenant has none. */
export function useTenantHome() {
  return useQuery({
    queryKey: TENANT_HOME_KEY,
    queryFn: () =>
      getOrNull<TenantHome>(`${TENANT_PREFERENCES}/home-dashboard`),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Sets the tenant default home. Admin only; the server enforces it. */
export function useSetTenantHome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (homeReportId: string) =>
      api.put(`${TENANT_PREFERENCES}/home-dashboard`, { homeReportId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESOLVED_HOME_KEY });
      queryClient.invalidateQueries({ queryKey: TENANT_HOME_KEY });
    },
  });
}

/** Removes the tenant default home. Admin only; the server enforces it. */
export function useClearTenantHome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteIfPresent(`${TENANT_PREFERENCES}/home-dashboard`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESOLVED_HOME_KEY });
      queryClient.invalidateQueries({ queryKey: TENANT_HOME_KEY });
    },
  });
}

/** What one scope has stored, before resolution. Null means it inherits. */
export type ScopePreference = {
  homeReportId: string | null;
  navConfig: NavConfig | null;
};

/** Only the changed fields. A null navConfig resets the nav to inherited. */
export type PreferenceWrite = {
  homeReportId?: string;
  navConfig?: NavConfig | null;
};

/** Whose preference a write reaches. The tenant default has its own routes. */
export type PreferenceScope = 'group' | 'user';

/** Groups and users both take a home and a navigation, in the session tenant. */
export type ScopeWrite = {
  scope: PreferenceScope;
  scopeId: string;
} & PreferenceWrite;

const scopeBase = ({
  scope,
  scopeId,
}: {
  scope: PreferenceScope;
  scopeId: string;
}) =>
  `${DIRECTORY_BASE}/${scope === 'group' ? 'groups' : 'users'}/${scopeId}/preferences`;

/**
 * Sets a group's or another user's home and navigation in the session tenant.
 * Send only the fields that changed; each becomes its own request.
 */
export function useSetScopePreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scope,
      scopeId,
      homeReportId,
      navConfig,
    }: ScopeWrite) => {
      const base = scopeBase({ scope, scopeId });
      if (homeReportId !== undefined) {
        await api.put(`${base}/home-dashboard`, { homeReportId });
      }
      if (navConfig === null) {
        await deleteIfPresent(`${base}/navigation`);
      } else if (navConfig !== undefined) {
        await api.put(`${base}/navigation`, navConfig);
      }
    },
    onSuccess: () => {
      // The admin may be in the group they just edited, so their own home moves too.
      queryClient.invalidateQueries({ queryKey: HOME_PREFERENCE_KEY });
    },
  });
}

/**
 * Clears a group's or a user's home in the session tenant, so it follows the
 * inherited default. Navigation is a separate record, left alone.
 */
export function useClearScopeHome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      scope,
      scopeId,
    }: {
      scope: PreferenceScope;
      scopeId: string;
    }) => deleteIfPresent(`${scopeBase({ scope, scopeId })}/home-dashboard`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOME_PREFERENCE_KEY });
    },
  });
}

/** A scope's stored defaults, plus where a user's home and nav come from. */
export type ScopeDefaults = ScopePreference & {
  effectiveHomeReportId?: string | null;
  effectiveSource?: PreferenceSource;
  effectiveSourceGroupId?: string | null;
  effectiveNavConfig?: NavConfig | null;
  effectiveNavSource?: PreferenceSource;
  effectiveNavSourceGroupId?: string | null;
};

export const scopePreferenceKey = ({
  scope,
  scopeId,
}: {
  scope: PreferenceScope;
  scopeId: string;
}) => [...HOME_PREFERENCE_KEY, 'scope', scope, scopeId];

type StoredHome = { homeReportId: string };
type StoredNavigation = { order: string[]; hidden: string[] };

/**
 * What a group or a user has in the session tenant, and for a user what they
 * end up with. Three reads: the home row, the navigation row, and for a user
 * the resolved chain the admin card explains.
 */
export function useScopePreference({
  scope,
  scopeId,
  enabled,
}: {
  scope: PreferenceScope;
  scopeId: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: scopePreferenceKey({ scope, scopeId }),
    queryFn: async (): Promise<ScopeDefaults> => {
      const base = scopeBase({ scope, scopeId });
      const [home, navigation, resolved] = await Promise.all([
        getOrNull<StoredHome>(`${base}/home-dashboard`),
        getOrNull<StoredNavigation>(`${base}/navigation`),
        scope === 'user'
          ? api.get<ResolvedHome>(`${base}/resolved`).then((res) => res.data)
          : null,
      ]);
      return {
        homeReportId: home?.homeReportId ?? null,
        navConfig: navigation
          ? { order: navigation.order, hidden: navigation.hidden }
          : null,
        ...(resolved && {
          effectiveHomeReportId: resolved.homeReportId,
          effectiveSource: resolved.source,
          effectiveSourceGroupId: resolved.sourceGroupId ?? null,
          effectiveNavConfig: resolved.navConfig,
          effectiveNavSource: resolved.navSource,
          effectiveNavSourceGroupId: resolved.navSourceGroupId ?? null,
        }),
      };
    },
    enabled: enabled && Boolean(scopeId),
    staleTime: 60000,
    // refetchOnMount is off globally; without this the card opens on stale data.
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
